// iter216 · CustomCodeInjector
//
// Applies three admin-authored code fragments on the home page (`/`) only:
//   • custom_code_head        → parsed into DOM nodes and appended to
//                                <head> (so <script> tags actually execute).
//   • custom_code_body_start  → rendered at #ccb-start (top of body).
//   • custom_code_body_end    → rendered at #ccb-end   (bottom of body).
//
// Guards:
//   • Not injected on /admin/* routes.
//   • Not injected on any route other than "/".
//   • Cleans up all injected nodes on unmount (route change) so
//     tracking pixels don't leak between navigations.
//
// Fetches once from /api/public-settings — the same endpoint everyone
// else uses, so no extra request cost on repeated mounts.
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL;
let _cache = null;    // { head, body_start, body_end }
let _fetchInflight = null;

async function loadCustomCode() {
  if (_cache) return _cache;
  if (_fetchInflight) return _fetchInflight;
  _fetchInflight = axios.get(`${BASE}/api/public-settings`, { timeout: 6000 })
    .then(({ data }) => {
      _cache = {
        head: (data?.custom_code_head || "").trim(),
        body_start: (data?.custom_code_body_start || "").trim(),
        body_end: (data?.custom_code_body_end || "").trim(),
      };
      return _cache;
    })
    .catch(() => ({ head: "", body_start: "", body_end: "" }))
    .finally(() => { _fetchInflight = null; });
  return _fetchInflight;
}

/**
 * Parse an HTML string into a NodeList, then re-create each <script>
 * so browsers actually execute it (innerHTML alone won't run scripts).
 */
function injectAt(root, html, marker) {
  if (!html) return [];
  const template = document.createElement("template");
  template.innerHTML = html;
  const inserted = [];
  Array.from(template.content.childNodes).forEach((node) => {
    if (node.nodeName === "SCRIPT") {
      const s = document.createElement("script");
      Array.from(node.attributes).forEach((a) => s.setAttribute(a.name, a.value));
      s.text = node.textContent || "";
      s.setAttribute("data-habano-cc", marker);
      root.appendChild(s);
      inserted.push(s);
    } else {
      const clone = node.cloneNode(true);
      if (clone.setAttribute) clone.setAttribute("data-habano-cc", marker);
      root.appendChild(clone);
      inserted.push(clone);
    }
  });
  return inserted;
}

export default function CustomCodeInjector() {
  const { pathname } = useLocation();
  useEffect(() => {
    // Guards: home only, never admin.
    if (pathname !== "/" || pathname.startsWith("/admin")) return undefined;
    let mounted = true;
    let injected = [];

    loadCustomCode().then((cc) => {
      if (!mounted) return;
      const bodyStartHost = document.getElementById("ccb-start");
      const bodyEndHost = document.getElementById("ccb-end");
      injected = [
        ...injectAt(document.head, cc.head, "head"),
        ...(bodyStartHost ? injectAt(bodyStartHost, cc.body_start, "body-start") : []),
        ...(bodyEndHost ? injectAt(bodyEndHost, cc.body_end, "body-end") : []),
      ];
    });

    return () => {
      mounted = false;
      injected.forEach((n) => { try { n.parentNode && n.parentNode.removeChild(n); } catch { /* ignore */ } });
      injected = [];
      // Belt-and-suspenders: strip any leftover marked node in the whole document.
      document.querySelectorAll('[data-habano-cc]').forEach((n) => {
        try { n.parentNode && n.parentNode.removeChild(n); } catch { /* ignore */ }
      });
    };
  }, [pathname]);
  return null;
}
